import React, { useCallback, useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiIconButton,
  MiInputTextbox,
  MiList,
  MiStackedTypography,
} from "@miplatform/components";
import { PAGINATION } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { ButtonVariants, IPaymentListItemResponse } from "@miplatform/types";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { mdiMagnify, mdiPlus, mdiPrinterOutline } from "@mdi/js";
import { Box, Divider, InputAdornment, Theme, Typography } from "@mui/material";
import { QuickBooksIcon } from "../../AccountingList/QuickBooksIcon";
import { PaymentListFilterComponent } from "./PaymentListFilterComponent";
import { FIELD_WIDTH, Field, LIST_CONFIG } from "./constants";
import { usePaymentListViewModel } from "./usePaymentListViewModel";

export const PaymentList: React.FC = () => {
  const { classes, theme } = useStyles();
  const { navigate } = useRouter();

  const {
    isLoading,
    search,
    debouncedSearch,
    setSearch,
    getPayments,
    setPayments,
    payments,
    customers: customerOptions,
    getCustomers,
    getTenant,
    handlePrint,
    getBankAccounts,
    bankAccountsOptions,
    filters,
    setFilters,
  } = usePaymentListViewModel();

  useEffect(() => {
    Promise.all([getCustomers(), getTenant(), getBankAccounts()]);
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value);
  };

  const LIST_DATA_CONFIG = {
    data: payments,
    setData: setPayments,
    dataProvider: getPayments,
  };

  const renderTitle = (item: IPaymentListItemResponse) => {
    const { id, paymentNumber } = item;
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/accounting/payments/${id}`);
    };
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          variant="body1"
          fontWeight={600}
          color="primary.main"
          component="a"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={handleClick}
        >
          {paymentNumber || " "}
        </Typography>
        <QuickBooksIcon
          order={{
            ...item,
            quickBooksStatus: item.quickBooksAuditDetails?.status,
          }}
          invoices={undefined}
        />
      </Box>
    );
  };

  const sections: any[] = [
    {
      fieldsetStyle: {
        justifyContent: "flex-end",
        alignItems: "center",
      },
      fieldset: [
        {
          renderCell: (row: IPaymentListItemResponse) => {
            return (
              <Box
                position="relative"
                display="flex"
                flexWrap="wrap"
                justifyContent="flex-end"
                gap={theme.spacing(2)}
              >
                {LIST_CONFIG.fields.map((field: Field) => {
                  const { label, fieldKey, width, type, valueGetter } = field;

                  return (
                    <MiStackedTypography
                      key={fieldKey}
                      title={label}
                      titleProps={{ width: width || FIELD_WIDTH }}
                      description={
                        valueGetter
                          ? valueGetter(row)
                          : parseFieldValue(
                              getNestedValue(row, fieldKey),
                              label.toLowerCase().includes("date")
                                ? "--/--/--"
                                : undefined,
                              undefined,
                              {
                                isCurrency: type === "currency",
                                currency: "USD",
                              }
                            )
                      }
                      descriptionProps={{
                        width: width || FIELD_WIDTH,
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Box>
            );
          },
        },
      ],
    },
  ];

  const CARD_LEFT_WIDTH = 150;
  const CARD_LEFT_MAX_WIDTH = 250;

  const createMiList = useCallback(
    (isPrint = false) => (
      <MiList
        showGroupCount
        viewType="card"
        loading={isPrint ? false : isLoading}
        cardProps={{
          sections,
          renderTitle,
          leftCardProps: {
            leftWidth: CARD_LEFT_WIDTH,
            leftMaxWidth: CARD_LEFT_MAX_WIDTH,
          },
        }}
        data={LIST_DATA_CONFIG.data}
        setData={LIST_DATA_CONFIG.setData}
        dataProvider={LIST_DATA_CONFIG.dataProvider}
        refresh={filters}
        search={debouncedSearch}
        paginationMode={PAGINATION.SERVER}
        hideListPagination={isPrint}
        defaultPageSize={25}
        emptyListProps={{
          text: "No Payments found",
        }}
      />
    ),
    [LIST_DATA_CONFIG, isLoading, filters, debouncedSearch]
  );

  const displayList = useMemo(() => createMiList(false), [createMiList]);
  const printList = useMemo(() => createMiList(true), [createMiList]);

  const handlePrintClick = useCallback(() => {
    handlePrint(printList);
  }, [handlePrint, printList]);

  const canPrint = useMemo(
    () => !isLoading && payments.length > 0,
    [isLoading, payments.length]
  );
  return (
    <>
      <MiBuildPageHeader
        title="Payments"
        loading={isLoading}
        actionComponent={
          <Box className={classes.actionContainer}>
            <MiInputTextbox
              value={search}
              style={{
                width: 220,
                marginLeft: theme.spacing(2),
                backgroundColor: theme.palette.common.white,
              }}
              handleChange={handleSearch}
              placeholder="Search Payments"
              textfieldProps={{
                style: {
                  fontSize:
                    (theme as unknown as { fontSize: { medium: number } })
                      .fontSize?.medium || 14,
                  height: 34,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <MiIcon path={mdiMagnify} size={0.8} color="grey" />
                  </InputAdornment>
                ),
              }}
            />
            <MiButton
              title="Post Payment"
              variant={ButtonVariants.Contained}
              startIcon={{ path: mdiPlus }}
              onClick={() => navigate("/accounting/payments/new")}
            />
            <MiIconButton
              data-testid="mi-orders-print-icon-button"
              disabled={!canPrint}
              onClick={handlePrintClick}
              className={!canPrint ? classes.disabledIconButton : undefined}
            >
              <MiIcon path={mdiPrinterOutline} color="grey" />
            </MiIconButton>
          </Box>
        }
      />

      <MiBox
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          height: "100%",
          overflow: "hidden",
          padding: 4,
          gap: 4,
        }}
      >
        <Box
          height="100%"
          flex={0.8}
          sx={{ overflow: "auto", display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ flex: 1, overflow: "auto" }}>{displayList}</Box>
        </Box>
        <Divider orientation="vertical" flexItem />

        <PaymentListFilterComponent
          customerOptions={customerOptions}
          bankAccountsOptions={bankAccountsOptions}
          filters={filters}
          setFilters={setFilters}
        />
      </MiBox>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  actionContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  disabledIconButton: {
    opacity: 0.4,
    pointerEvents: "none",
  },
}));
